/**
 * ShapeMedia — wraps any UI element (image, video, button, div) with an MD3
 * Expressive shape clip-path, with optional shape morphing animation.
 *
 * @example
 * // Static shape
 * <ShapeMedia shape="sunny" width={200} height={200}>
 *   <img src="/photo.jpg" alt="A sunny photo" />
 * </ShapeMedia>
 *
 * @example
 * // Shape morph on hover
 * <ShapeMedia shape="circle" morphTo="flower" morphOn="hover" width={200} height={200}>
 *   <img src="/avatar.jpg" alt="Avatar" />
 * </ShapeMedia>
 */

import { type CSSProperties, useEffect, useRef, useState } from "react";
import type { ShapeMediaProps } from "./types";
import { useShapeMorph } from "./useShapeMorph";

/**
 * ShapeMedia wraps children in a shape clip-path with optional morphing animation.
 *
 * @remarks
 * - Requires "use client" (uses ResizeObserver, animation APIs).
 * - Use ShapeMediaServer for SSR-only static rendering.
 * - Automatically handles `prefers-reduced-motion`.
 *
 * @param props - {@link ShapeMediaProps}
 */
export function ShapeMedia({
	shape,
	morphTo,
	morphOn = "hover",
	morphOptions,
	scrollProgress,
	width: widthProp,
	height: heightProp,
	children,
	className,
	style,
	"aria-label": ariaLabel,
	role,
	disabled,
}: ShapeMediaProps): React.JSX.Element {
	const containerRef = useRef<HTMLDivElement>(null);
	const [measuredWidth, setMeasuredWidth] = useState(widthProp ?? 0);
	const [measuredHeight, setMeasuredHeight] = useState(heightProp ?? 0);

	const width = widthProp ?? measuredWidth;
	const height = heightProp ?? measuredHeight;

	// ─── ResizeObserver: auto-measure if no explicit dimensions ──────────
	useEffect(() => {
		if (widthProp !== undefined && heightProp !== undefined) return;
		const el = containerRef.current;
		if (!el) return;
		const observer = new ResizeObserver(([entry]) => {
			const { width: w, height: h } = entry.contentRect;
			setMeasuredWidth(Math.round(w));
			setMeasuredHeight(Math.round(h));
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [widthProp, heightProp]);

	// ─── Shape morph ──────────────────────────────────────────────────────
	const { clipPath, handlers } = useShapeMorph({
		shape,
		morphTo,
		morphOn,
		morphOptions,
		scrollProgress,
		width: Math.max(width, 1),
		height: Math.max(height, 1),
		disabled,
	});

	// ─── Accessibility ────────────────────────────────────────────────────
	const resolvedRole = role ?? (ariaLabel ? "img" : "presentation");
	const accessibilityProps = ariaLabel
		? { role: resolvedRole, "aria-label": ariaLabel }
		: { role: resolvedRole };

	const computedStyle: CSSProperties = {
		...style,
		clipPath,
		overflow: "hidden",
		position: "relative",
	};

	return (
		<div
			ref={containerRef}
			className={className}
			style={computedStyle}
			{...accessibilityProps}
			{...handlers}
		>
			{children}
		</div>
	);
}
